import React, { useCallback, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import {
  ChatStates,
  selectChatState,
  showEndSessionModalSelector,
  virtualHumanTalkingSelector,
} from '../../../../slices/chat/ChatSlice';
import { usePrevious } from '../../../../utility/StateHooks';
import RecordingButton from '../../RecordingButton/RecordingButton';
import * as Styled from './DeepgramButton.styled';
import { languageSelector } from '../../../../slices/PatientSlice';
import { useDebounce } from 'use-debounce';
import { MicMutedContext } from '../../MicMutedProvider.context';
import { MicrophoneStreamContext } from '@core/components/MicrophoneStreamProvider/MicrophoneStreamProvider';
import { useUpdateMicActive } from '@vh/components/VHChatInputArea/VHTextChatInput/useUpdateMicActive';
import * as Sentry from '@sentry/browser';
import { useDisconnectNotify } from '@vh/utility/useDisconnectNotify';
import { useDeepgram } from '@vh/utility/useDeepgram';
import useFeature from '@core/components/LaunchDarkly';

/**
 * @type {import('react').FC<{
 *  onSubmit: (chat: string) => void;
 *  onChange: (e: string) => void;
 *  textDisabled: boolean;
 *  recordingDisabled: boolean;
 *  onSpeechError: (err: string) => void
 *  design: "fill" | "outline" | "transparent" | "text";
 * }>}
 */

const DeepgramButton = ({
  onSubmit,
  onChange,
  textDisabled,
  recordingDisabled,
  onSpeechError,
  design,
  onDisableChat,
}) => {
  const { muted, setMuted } = useContext(MicMutedContext);
  const {
    setVolume,
    error: micError,
    stream,
  } = useContext(MicrophoneStreamContext);
  const chatState = useSelector(selectChatState);
  const virtualHumanTalking = useSelector(virtualHumanTalkingSelector);
  const language = useSelector(languageSelector);
  const chatInputRef = useRef(null);
  const chatLoadingStatus = useSelector((state) => state.chat.status);
  const showMutedNoticeIfTalked =
    muted &&
    !virtualHumanTalking &&
    chatLoadingStatus !== 'pending' &&
    chatState !== ChatStates.LOADING;
  const showingEndSessionModal = useSelector(showEndSessionModalSelector);
  const pauseDeepgram =
    muted ||
    textDisabled ||
    virtualHumanTalking ||
    chatLoadingStatus === 'pending' ||
    chatState === ChatStates.LOADING ||
    showingEndSessionModal;
  // use debounced variable so that it doesn't flick between paused and unpaused in the moment that they change
  const [debouncedPauseDeepgram] = useDebounce(pauseDeepgram, 10);
  const { incrementDisconnects } = useDisconnectNotify();

  const handleCloseSocket = useCallback(
    (closeCode, closeReason) => {
      if (closeCode !== 1000) {
        setMuted(true);
        if (closeReason) {
          incrementDisconnects(closeReason);
        }
      }
    },
    [incrementDisconnects, setMuted]
  );

  const { finalAffirmation, error, connected, currentTranscript, connecting } =
    useDeepgram({
      language,
      paused: debouncedPauseDeepgram,
      onClose: handleCloseSocket,
    });

  const toggleConnection = useCallback(
    (newMuted) => {
      if (!recordingDisabled) {
        setMuted(newMuted);
      }
    },
    [recordingDisabled, setMuted]
  );

  const handleRecordingButtonClick = useCallback(
    (newMutedState) => {
      toggleConnection(newMutedState);
    },
    [toggleConnection]
  );

  const prevConnected = usePrevious(connected);
  useEffect(() => {
    if (connected && !prevConnected && muted && !micError && stream) {
      setMuted(false);
    }
  }, [
    muted,
    toggleConnection,
    setMuted,
    prevConnected,
    connected,
    micError,
    stream,
  ]);

  useEffect(() => {
    setVolume(debouncedPauseDeepgram ? 0 : 1);
  }, [setVolume, debouncedPauseDeepgram]);

  const pausedTranscriptions = muted || textDisabled || virtualHumanTalking;

  // if mic stops listening and a transcript exists, submit that newly spoken text
  const prevFinalAffirmation = usePrevious(finalAffirmation);
  useEffect(() => {
    if (
      !pausedTranscriptions &&
      finalAffirmation !== prevFinalAffirmation &&
      !muted &&
      !virtualHumanTalking &&
      !textDisabled
    ) {
      onSubmit(finalAffirmation);
    }
  }, [
    finalAffirmation,
    onSubmit,
    virtualHumanTalking,
    prevFinalAffirmation,
    textDisabled,
    muted,
    pausedTranscriptions,
  ]);

  useEffect(() => {
    if (currentTranscript && !debouncedPauseDeepgram) {
      onChange(currentTranscript);
    }
  }, [onChange, currentTranscript, debouncedPauseDeepgram]);

  const deepgramSDKFeature = useFeature('use-deepgram-sdk');

  // when using the SDK then handle an extra parameter for disabling chat inputs
  useEffect(() => {
    if (onDisableChat && deepgramSDKFeature) {
      onDisableChat(
        connecting || virtualHumanTalking || (!pauseDeepgram && !connected)
      );
    }
  }, [
    connecting,
    onDisableChat,
    virtualHumanTalking,
    deepgramSDKFeature,
    pauseDeepgram,
    connected,
  ]);

  useEffect(() => {
    if (error) {
      onSpeechError(error);
      setMuted(true);
      console.error(error);
      try {
        Sentry.captureException(error);
      } catch (e) {
        console.error(e);
      }
    }
  }, [error, setMuted, onSpeechError]);

  useUpdateMicActive(!muted);

  return (
    <RecordingButton
      disabled={recordingDisabled}
      onClick={handleRecordingButtonClick}
      muted={muted}
      connected={connected}
      showMutedNoticeIfTalked={showMutedNoticeIfTalked}
      networkError={!connected && !!error}
      connecting={connecting}
      design={design}
      loading={false}
      recording={false}
    />
  );
};

export default DeepgramButton;
